import React, { useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { AddInventoryProducts } from "../../../InventoryServices/InventoryAPI/AddInventoryProducts";

const RelatedAddDesign = ({ isOpen, onClose, parentInventoryId }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    dropdownValue: "",
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategorySelect = (value) => {
    setFormData((prev) => ({ ...prev, dropdownValue: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fd = new FormData();
      fd.append("title", formData.name);
      fd.append("description", formData.description);
      fd.append("category", formData.dropdownValue);
      fd.append("related_inventory_id", parentInventoryId); // important!

      const res = await AddInventoryProducts(fd);

      if (res.status === 200 || res.status === 201) {
        toast({
          title: "Design created successfully!",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        onClose();
        setFormData({ name: "", description: "", dropdownValue: "" });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Failed to create design",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const displayCategory = formData.dropdownValue
    ? formData.dropdownValue.charAt(0).toUpperCase() +
      formData.dropdownValue.slice(1)
    : "Select category";

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent borderRadius="0" bg="white" shadow="xl">
        <DrawerCloseButton mt={2} />
        <DrawerHeader
          bg="blue.600"
          color="white"
          fontWeight="semibold"
          fontSize="lg"
        >
          Create New Design
        </DrawerHeader>

        <form onSubmit={handleSubmit}>
          <DrawerBody px={6} py={6}>
            <VStack spacing={6} align="stretch">
              <HStack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                    Design Name
                  </FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter Design Name"
                    borderRadius="lg"
                    bg="gray.50"
                    borderColor="gray.200"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                    Category
                  </FormLabel>
                  <Menu closeOnSelect>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      textAlign="left"
                      borderRadius="lg"
                      bg="gray.50"
                      borderColor="gray.200"
                      borderWidth="1px"
                      _hover={{ bg: "gray.100" }}
                      _expanded={{ bg: "gray.100" }}
                      _focus={{ boxShadow: "outline" }}
                      width="100%"
                    >
                      {displayCategory}
                    </MenuButton>
                    <MenuList maxHeight="200px" overflowY="auto">
                      <MenuItem onClick={() => handleCategorySelect("floorplan")}>
                        Floorplan
                      </MenuItem>
                      <MenuItem onClick={() => handleCategorySelect("elevation")}>
                        Elevation
                      </MenuItem>
                      <MenuItem onClick={() => handleCategorySelect("electrical")}>
                        Electrical
                      </MenuItem>
                      <MenuItem onClick={() => handleCategorySelect("plumbing")}>
                        Plumbing
                      </MenuItem>
                      <MenuItem onClick={() => handleCategorySelect("drainage")}>
                        Drainage
                      </MenuItem>
                      <MenuItem onClick={() => handleCategorySelect("2d")}>
                        2D
                      </MenuItem>
                      <MenuItem onClick={() => handleCategorySelect("3d")}>
                        3D
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Description
                </FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Write a design description..."
                  rows={4}
                  borderRadius="lg"
                  bg="gray.50"
                  borderColor="gray.200"
                />
              </FormControl>
            </VStack>
          </DrawerBody>

          <DrawerFooter
            px={6}
            py={4}
            bg="gray.50"
            borderTop="1px solid"
            borderColor="gray.200"
          >
            <Button
              variant="outline"
              colorScheme="gray"
              mr={3}
              borderRadius="md"
              onClick={onClose}
              _hover={{ bg: "gray.100" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={loading}
              bg="blue.600"
              color="white"
              fontWeight="medium"
              borderRadius="md"
              px={6}
              _hover={{ bg: "blue.700", shadow: "md" }}
            >
              Create Design
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};

export default RelatedAddDesign;
